<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, toRefs } from "vue";
import DocumentDocxView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/DocumentView/DocumentDocxView.vue";
import DocumentHeicView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/DocumentView/DocumentHeicView.vue";
import DocumentImageView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/DocumentView/DocumentImageView.vue";
import DocumentPDFView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/DocumentView/DocumentPDFView.vue";
import SubmissionDownloadButton from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/SubmissionDownloadButton/index.vue";
import SubmissionFileUnavailable from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/SubmissionFileUnavailable.vue";
import {
  canPreviewFile,
  getSubmissionFileName,
  isDocxFile,
  isHeicFile,
  isImageFile,
  isPdfFile,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/utils";
import { useSubmissionFilePresign } from "@/components/MyStudents/Submissions/composables/useSubmissionFilePresign";
import ArrowUpRight from "@/components/shared/icons/ArrowUpRight.vue";
import FileIcon from "@/components/shared/icons/FileIcon.vue";
import RoundedCheckMark from "@/components/shared/icons/RoundedCheckMark.vue";

import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";

const props = defineProps({
  documents: {
    type: Array,
    required: true,
  },
  submissionId: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["open-preview"]);

const { documents, submissionId } = toRefs(props);

const { mutateAsync: presignSubmissionFile } = useSubmissionFilePresign();

const loadedUrlDocuments = ref([]);
const isLoading = ref(true);

const hasDocuments = computed(() => loadedUrlDocuments.value.length > 0);

const openPreview = (file) => {
  emit("open-preview", file);
};

// A URL can be minted for a file the browser then cannot read. Dropping it here
// falls through to the unavailable message instead of a preview that never loads.
const handlePreviewError = (failed) => {
  loadedUrlDocuments.value = loadedUrlDocuments.value.filter((entry) => entry.url !== failed.url);
};

const loadDocument = async (doc) => {
  if (!doc?.bucket || !doc?.key) {
    return undefined;
  }
  const filename = getSubmissionFileName(doc);

  // Signing a file nothing can render would record it as viewed when it never was.
  if (!canPreviewFile(filename)) {
    return { file: doc, filename, url: undefined, isPreviewable: false };
  }

  try {
    const url = await presignSubmissionFile({
      bucket: doc.bucket,
      key: doc.key,
      submissionId: submissionId.value,
    });
    if (!url) {
      return undefined;
    }
    return { file: doc, filename, url, isPreviewable: true };
  } catch {
    // A file that cannot be signed for is reported as unavailable rather than
    // left behind a loader that never resolves.
    return undefined;
  }
};

onMounted(async () => {
  const entries = await Promise.all((documents.value ?? []).map(loadDocument));
  loadedUrlDocuments.value = entries.filter(Boolean);
  isLoading.value = false;
});
</script>

<template>
  <div>
    <SkeletonLoader v-if="isLoading" height="h-24" data-testid="my-students-submission-current-documents-loader" />
    <SubmissionFileUnavailable v-else-if="!hasDocuments" />
    <div v-else class="flex md:flex-row flex-col min-h-72 self-start">
      <div
        v-for="(entry, index) in loadedUrlDocuments"
        :key="entry?.file?.key ?? index"
        class="flex flex-col md:w-72 shadow-md rounded-lg overflow-hidden mt-4 md:mt-0 md:ml-4"
      >
        <component
          :is="entry?.isPreviewable ? 'button' : 'div'"
          :type="entry?.isPreviewable ? 'button' : undefined"
          class="group relative flex h-72 w-full items-center justify-center overflow-hidden bg-white p-4"
          :data-testid="
            entry?.isPreviewable
              ? 'my-students-submission-current-document-preview'
              : 'my-students-submission-current-document-static'
          "
          @click="entry?.isPreviewable && openPreview(entry?.file)"
        >
          <DocumentPDFView
            v-if="isPdfFile(entry?.filename)"
            :url="entry?.url"
            :allow-download="false"
            class="w-full h-full block overflow-hidden"
            @error="handlePreviewError(entry)"
          />
          <DocumentImageView
            v-else-if="isImageFile(entry?.filename)"
            :name="entry?.filename"
            :url="entry?.url"
            :allow-download="false"
            @error="handlePreviewError(entry)"
          />
          <DocumentDocxView
            v-else-if="isDocxFile(entry?.filename)"
            :url="entry?.url"
            class="w-full h-full block overflow-hidden pointer-events-none"
            @error="handlePreviewError(entry)"
          />
          <DocumentHeicView
            v-else-if="isHeicFile(entry?.filename)"
            :name="entry?.filename"
            :url="entry?.url"
            @error="handlePreviewError(entry)"
          />
          <FileIcon v-else class="w-16 h-16" />

          <template v-if="entry?.isPreviewable">
            <span
              class="pointer-events-none absolute inset-0 bg-indigo-base/30 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            />
            <span
              class="pointer-events-none absolute inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-indigo-base shadow-lg opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            >
              <ArrowUpRight custom-class="w-4 h-4" />
              Click to preview
            </span>
          </template>
        </component>
        <div class="flex items-center gap-2 border-t border-gray-175 px-4 py-3">
          <RoundedCheckMark class="w-5 h-5 shrink-0" />
          <span
            class="flex-1 font-semibold text-xs text-pretty break-all"
            data-testid="my-students-submission-current-documents-filename"
            >{{ entry?.filename }}</span
          >
          <SubmissionDownloadButton
            :file="entry?.file"
            :submission-id="submissionId"
            data-testid="my-students-submission-current-document-download"
          />
        </div>
      </div>
    </div>
  </div>
</template>
